/**
 * Compact colored pill for each credit bureau. Handles cross-bureau duplicates
 * cleanly — pass a `bureaus` array and it renders one badge per bureau plus a
 * subtle "reported by all three" indicator when the account appears on every
 * bureau (a strong hint the account is real vs a mixed-file error).
 */
const BUREAU_META = {
  equifax:    { short: 'EQ', label: 'Equifax',    bg: '#B01F24', fg: '#FFFFFF' },
  experian:   { short: 'EX', label: 'Experian',   bg: '#0055A5', fg: '#FFFFFF' },
  transunion: { short: 'TU', label: 'TransUnion', bg: '#4A2C82', fg: '#FFFFFF' },
  unknown:    { short: '??', label: 'Unknown',    bg: '#8C8580', fg: '#FFFFFF' },
};

const normalize = (b) => {
  const s = String(b || '').toLowerCase().replace(/[^a-z]/g, '');
  if (s.startsWith('equi')) return 'equifax';
  if (s.startsWith('exper')) return 'experian';
  if (s.startsWith('trans')) return 'transunion';
  return 'unknown';
};

export default function BureauBadges({ bureaus, size = 'sm', showLabel = false, className = '' }) {
  const list = Array.isArray(bureaus) ? bureaus : bureaus ? [bureaus] : [];
  if (list.length === 0) return null;
  const uniqueKeys = [...new Set(list.map(normalize))];
  const isAllThree = uniqueKeys.length === 3 &&
    uniqueKeys.includes('equifax') &&
    uniqueKeys.includes('experian') &&
    uniqueKeys.includes('transunion');
  const px = size === 'xs' ? 'px-1.5' : 'px-2';
  const py = size === 'xs' ? 'py-0.5' : 'py-0.5';
  const fs = size === 'xs' ? 'text-[10px]' : 'text-xs';

  return (
    <div className={`flex flex-wrap items-center gap-1 ${className}`} data-testid="bureau-badges">
      {uniqueKeys.map((k) => {
        const m = BUREAU_META[k];
        return (
          <span
            key={k}
            className={`${px} ${py} ${fs} rounded font-['Manrope'] font-semibold whitespace-nowrap`}
            style={{ background: m.bg, color: m.fg, letterSpacing: '0.02em' }}
            title={`Reported by ${m.label}`}
            data-testid={`bureau-badge-${k}`}
          >
            {showLabel ? m.label : m.short}
          </span>
        );
      })}
      {isAllThree && (
        <span
          className="text-[10px] text-[#36454F]/55 font-['Manrope'] italic ml-0.5 whitespace-nowrap"
          data-testid="all-three-hint"
        >
          on all three bureaus
        </span>
      )}
      {!isAllThree && uniqueKeys.length > 1 && (
        <span
          className="text-[10px] text-[#36454F]/55 font-['Manrope'] italic ml-0.5 whitespace-nowrap"
          data-testid="multi-bureau-hint"
        >
          {uniqueKeys.length} bureaus
        </span>
      )}
    </div>
  );
}
